/*
 * File: BulkVendorInput.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 17 Feb 2022 3:41:16 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { Select, Space, Spin } from 'antd';
import { InlineStylesModel } from 'models/InlineStylesModel';
import React, { FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setBulkVendorValue } from 'redux/productsInfo/bulkApplySlice';
import { ReduxState } from 'redux/rootReducer';
import { useGetVendorsQuery } from 'redux/services/useSimonSoundman';
import { useDebouncedCallback } from 'use-debounce';
import { BlockLoader } from '../BlockLoader';

const styles: InlineStylesModel = {
  formItem: {
    marginBottom: 0
  },
  select: {
    width: '170px'
  }
};

export const BulkVendorInput: FC = () => {
  const { selectedCompanyDataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const [offset, _setOffset] = useState(0);
  const [searchValue, _setSearchValue] = useState<string | undefined>(undefined);
  const dispatch = useDispatch();

  /* ****************** API Hooks ****************** */
  const { data: vendorResponse, isLoading, isFetching } = useGetVendorsQuery({ dataAreaId: selectedCompanyDataAreaId ?? '', offset, vendorAccountContains: searchValue });

  /* ****************** JSX / Variables ****************** */
  const vendorsOptions = useMemo(
    () => vendorResponse?.data?.map((vendor) => ({ value: vendor.vendorAccountNumber, label: `${vendor.vendorAccountNumber} - ${vendor.vendorName}` })).concat({ value: '', label: '' }),
    [vendorResponse]
  );

  /* ****************** Functions ****************** */
  // Handle delay of searching data
  const handleSearch = useDebouncedCallback((value: string | undefined) => {
    _setOffset(0);
    _setSearchValue(value);
  }, 400);

  const handleClear = (): void => {
    _setSearchValue(undefined);
    _setOffset(0);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;

    if (!isLoading && !isFetching && target.scrollTop + target.offsetHeight === target.scrollHeight && vendorResponse && vendorResponse?.totalCount - offset > 25) {
      _setOffset((prevState) => prevState + 25);
    }
  };

  const handleSelect = (value: string): void => {
    dispatch(setBulkVendorValue(value));
  };

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    if ((isLoading || isFetching) && !offset)
      return (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning />
        </div>
      );

    const moreResultsJSX =
      (isLoading || isFetching) && offset ? (
        <div style={{ padding: '0px 10px' }}>
          <Space>
            <Spin size="small" indicator={<BlockLoader direction="loader loader--slideUp" />} />
          </Space>
        </div>
      ) : null;

    return (
      <div>
        {menu}
        {moreResultsJSX}
      </div>
    );
  };

  /* ****************** Render ****************** */
  return (
    <Select
      allowClear
      placeholder="Select a Vendor"
      dropdownMatchSelectWidth={false}
      dropdownRender={dropDownRender}
      filterOption={false}
      loading={isLoading || isFetching}
      onClear={handleClear}
      onPopupScroll={handleScroll}
      onSearch={handleSearch}
      onSelect={handleSelect}
      options={vendorsOptions}
      showSearch
      style={styles.select}
    />
  );
};
